import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Calendar, ChevronRight, FileText, Loader2, Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
};

export default function HistoryPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/logs/my`);
      setLogs(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewLog = (date) => {
    navigate(`/app/log?date=${date}`);
  };

  const handleDeleteLog = async (date, e) => {
    e.stopPropagation();
    setDeletingId(date);
    try {
      await axios.delete(`${API}/logs/my/${date}`);
      toast.success("Log deleted successfully!");
      setLogs(logs.filter((log) => log.log_date !== date));
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to delete log";
      toast.error(message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateLog = () => {
    navigate("/app/log");
  };

  const groupLogsByMonth = () => {
    const grouped = {};
    logs.forEach((log) => {
      const monthYear = format(parseISO(log.log_date), "MMMM yyyy");
      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(log);
    });
    return grouped;
  };

  const groupedLogs = groupLogsByMonth();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Log History
          </h1>
          <p className="text-slate-600 mt-1">
            View your past daily work logs
          </p>
        </div>
        <Button onClick={handleCreateLog} data-testid="create-log-button">
          <Plus className="mr-2 h-4 w-4" />
          Create Log
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : logs.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No logs yet</h3>
            <p className="text-slate-500 mt-1">Start by creating your first daily log</p>
            <Button className="mt-4" onClick={handleCreateLog} data-testid="create-first-log-button">
              <Plus className="mr-2 h-4 w-4" />
              Create Log
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedLogs).map(([monthYear, monthLogs]) => (
            <div key={monthYear}>
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                {monthYear}
              </h2>
              <div className="space-y-3">
                {monthLogs.map((log) => (
                  <Card
                    key={log.id}
                    className="border-slate-200 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => handleViewLog(log.log_date)}
                    data-testid={`log-card-${log.log_date}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex flex-col items-center justify-center">
                            <span className="text-xs text-slate-500 uppercase">
                              {format(parseISO(log.log_date), "EEE")}
                            </span>
                            <span className="text-lg font-bold text-slate-700">
                              {format(parseISO(log.log_date), "d")}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">
                              {format(parseISO(log.log_date), "EEEE, MMMM d, yyyy")}
                            </p>
                            <p className="text-sm text-slate-500 line-clamp-1 max-w-md">
                              {stripHtml(log.tasks_today)?.substring(0, 80) || ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {log.is_editable ? (
                            <>
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                Editable
                              </Badge>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={deletingId === log.log_date}
                                    data-testid={`delete-log-${log.log_date}`}
                                  >
                                    {deletingId === log.log_date ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this log?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This action cannot be undone. This will permanently delete your log for {format(parseISO(log.log_date), "MMMM d, yyyy")}.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={(e) => handleDeleteLog(log.log_date, e)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          ) : (
                            <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
                              Read-only
                            </Badge>
                          )}
                          <ChevronRight className="h-5 w-5 text-slate-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
